// QR Kiosk v2 — KV2-8 guest redeem API. Binds the two ANONYMOUS B6/SP1 endpoints
// on the default apiBaseUrl (Account service — same base accountApi.ts uses):
//   POST /v1/account/guest/redeem       { code }
//   POST /v1/account/guest/redeem-pin   { machineInternalNumber, pinCode }
// Response contract is frozen by SP1 (BREAKING_CHANGES 2026-07-25) — consumed verbatim.
//
// Deliberately NOT core/api: the shared axios interceptor (a) masks the HTTP status
// into { message, code } (we need 429 vs other-4xx to choose a message) and (b) on a
// 401 calls accountContext.logout() + window.location = "/" — both wrong for an
// anonymous redeem. This bare instance sends no Authorization header and preserves
// err.response.status. It never logs the code/PIN.
import Axios from "axios";

export interface GuestRedeemResult {
  tokenType: string;
  accessToken: string;
  expiresIn: number; // JWT session seconds (min(12h, passExpiresAt))
  passExpiresAt: string; // ISO — the pass window (may outlast the JWT session)
  machineInternalNumber: string;
  guestName: string;
  ownerCompanyName: string;
}

const guestApi = Axios.create({ baseURL: (window as any).app.config.apiBaseUrl });

// The backend returns a UNIFORM 4xx for invalid / expired / machine-mismatch (no
// existence oracle, SP1) — so those collapse to one "invalid" kind on purpose.
// Only 429 (rate limiter) is distinguishable; anything else is treated as network.
export type GuestRedeemErrorKind = "rateLimited" | "invalid" | "network";

// A plain tagged failure (NOT a subclassed Error): the app compiles to es5, where
// `class X extends Error` loses the prototype chain and `instanceof` silently fails —
// that would collapse every redeem failure to "network" and lose the 429 signal.
export interface GuestRedeemFailure {
  isGuestRedeemFailure: true;
  kind: GuestRedeemErrorKind;
}

function fail(err: any): GuestRedeemFailure {
  const status = err?.response?.status;
  const kind: GuestRedeemErrorKind =
    status === 429 ? "rateLimited" : typeof status === "number" && status >= 400 && status < 500 ? "invalid" : "network";
  return { isGuestRedeemFailure: true, kind };
}

/** Read the kind off a thrown redeem failure (es5-safe — no instanceof). */
export function guestRedeemErrorKind(err: any): GuestRedeemErrorKind {
  const k = err?.kind;
  return k === "rateLimited" || k === "invalid" || k === "network" ? k : "network";
}

export async function redeemMagicLink(code: string): Promise<GuestRedeemResult> {
  try {
    const { data } = await guestApi.post<GuestRedeemResult>("/v1/account/guest/redeem", { code });
    return data;
  } catch (err) {
    throw fail(err);
  }
}

export async function redeemPin(machineInternalNumber: string, pinCode: string): Promise<GuestRedeemResult> {
  try {
    const { data } = await guestApi.post<GuestRedeemResult>("/v1/account/guest/redeem-pin", {
      machineInternalNumber,
      pinCode,
    });
    return data;
  } catch (err) {
    throw fail(err);
  }
}
